import { BRANDING_THEME as BRAND_COLORS } from "../../../constants/brandingTheme";
import { displayBusinessUrl } from "../../../utils/businessUrl";
import { PRICING, ADDON_LABELS, addonsChangePending } from "../pricing";
import PasswordInput from "../../../components/PasswordInput";

/** Add-ons contratables, en el orden del catálogo de precios. La etiqueta es la del superadmin. */
const ADDONS = Object.keys(PRICING.addons).map((key) => ({ key, label: ADDON_LABELS[key] || key }));

/**
 * Formulario de configuración general + suscripción de un cliente. Estado y guardado
 * viven en SuperadminPanel; aquí solo se edita `editingData`.
 */
export default function ClientConfigForm({ client, editingData, setEditingData, computePrice, getSubscriptionStatus, onApplyAddonsNow }) {
  const addonsPending = addonsChangePending(editingData);
  return (
    <>
      {/* Client Configuration */}
      <div className="mb-4">
        <h5 className="h6 d-flex align-items-center gap-2 mb-3" style={{ color: BRAND_COLORS.primary.main, fontSize: '0.9rem' }}>
          Configuración General
        </h5>
        <div className="superadmin-config-fields">
          <div className="app-form-group">
            <label className="app-form-label small" style={{ color: BRAND_COLORS.text.secondary, fontSize: '0.8rem' }}>Nombre del Negocio</label>
            <input
              className="app-form-control"
              value={editingData.businessName || ""}
              onChange={e => setEditingData(ed => ({ ...ed, businessName: e.target.value }))}
              placeholder="Ej: TurnLink Barbería"
            />
          </div>
          <div className="app-form-group">
            <label className="app-form-label small" style={{ color: BRAND_COLORS.text.secondary, fontSize: '0.8rem' }}>Username</label>
            <input
              className="app-form-control font-monospace"
              value={editingData.username || ""}
              onChange={e => setEditingData(ed => ({ ...ed, username: e.target.value }))}
              placeholder="Ej: turnlink"
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
            />
          </div>
          <div className="app-form-group">
            <label className="app-form-label small" style={{ color: BRAND_COLORS.text.secondary, fontSize: '0.8rem' }}>Prefijo del Cliente</label>
            <input
              className="app-form-control font-monospace"
              value={editingData.prefix || ""}
              onChange={e => setEditingData(ed => ({
                ...ed,
                prefix: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '')
              }))}
              placeholder="Ej: sb, bella, cm"
              maxLength="10"
            />
            <small style={{ color: BRAND_COLORS.text.muted, fontSize: '0.7rem' }}>Se usa para generar URLs únicas: {displayBusinessUrl(client.prefix || client.username)}</small>
          </div>
          <div className="app-form-group">
            <label className="app-form-label small" style={{ color: BRAND_COLORS.text.secondary, fontSize: '0.8rem' }}>Nueva Contraseña (opcional)</label>
            <PasswordInput
              className="app-form-control"
              value={editingData.password || ""}
              onChange={e => setEditingData(ed => ({ ...ed, password: e.target.value }))}
              placeholder="Dejar vacío para mantener actual"
              autoComplete="new-password"
              name="client-new-password"
            />
          </div>
          <div className="app-form-group">
            <label className="app-form-label small" style={{ color: BRAND_COLORS.text.secondary, fontSize: '0.8rem' }}>Estado</label>
            <select
              className="app-form-control"
              value={editingData.status ? "true" : "false"}
              onChange={(e) =>
                setEditingData((ed) => ({
                  ...ed,
                  status: e.target.value === "true",
                }))
              }
            >
              <option value="true">Activo</option>
              <option value="false">Inactivo</option>
            </select>
          </div>
          <div className="app-form-group">
            <label className="app-form-label small" style={{ color: BRAND_COLORS.text.secondary, fontSize: '0.8rem' }}>Asientos (profesionales / cuentas Google)</label>
            <input
              className="app-form-control"
              type="number"
              min="1"
              inputMode="numeric"
              value={editingData.seats ?? 1}
              onChange={(e) => setEditingData((ed) => ({ ...ed, seats: e.target.value }))}
              onBlur={(e) => setEditingData((ed) => ({ ...ed, seats: Math.max(1, parseInt(e.target.value, 10) || 1) }))}
            />
            <small style={{ color: BRAND_COLORS.text.muted, fontSize: '0.7rem' }}>
              Base ${PRICING.basePrice} (1 incluido) + ${PRICING.extraSeatPrice} por cada extra
            </small>
          </div>
          <div className="app-form-group">
            <label className="app-form-label small" style={{ color: BRAND_COLORS.text.secondary, fontSize: '0.8rem' }}>Tope del asistente IA (conversaciones / mes)</label>
            <input
              className="app-form-control"
              type="number"
              min="0"
              step="100"
              inputMode="numeric"
              value={editingData.aiMonthlyCap ?? 1500}
              onChange={(e) => setEditingData((ed) => ({ ...ed, aiMonthlyCap: e.target.value }))}
              onBlur={(e) => setEditingData((ed) => ({ ...ed, aiMonthlyCap: Math.max(0, parseInt(e.target.value, 10) || 0) }))}
            />
            <small style={{ color: BRAND_COLORS.text.muted, fontSize: '0.7rem' }}>
              Al llegar, el bot vuelve a sus menús hasta el próximo mes. 0 = sin tope. El negocio lo ve, no lo edita.
            </small>
          </div>
          {/* Los add-ons se programan: el checkbox edita addonsNextCycle y lo vigente (addons)
              solo cambia al registrar el pago, para no quitar a media mensualidad algo ya pagado. */}
          <div className="app-form-group">
            <label className="app-form-label small" style={{ color: BRAND_COLORS.text.secondary, fontSize: '0.8rem' }}>Add-ons (entran con el próximo pago)</label>
            <div className="d-flex flex-column gap-2">
              {ADDONS.map(({ key, label }) => {
                const today = !!editingData.addons?.[key];
                const next = !!editingData.addonsNextCycle?.[key];
                return (
                  <label key={key} className="form-check-label small d-flex align-items-center gap-2 mb-0 flex-wrap" style={{ color: BRAND_COLORS.text.secondary, fontSize: '0.8rem' }}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={next}
                      onChange={(e) =>
                        setEditingData((ed) => ({
                          ...ed,
                          addonsNextCycle: { ...ed.addonsNextCycle, [key]: e.target.checked },
                        }))
                      }
                      style={{ accentColor: BRAND_COLORS.primary.main }}
                    />
                    {label}
                    <span style={{ color: today !== next ? BRAND_COLORS.warning : BRAND_COLORS.text.muted, fontSize: '0.7rem' }}>
                      {today !== next
                        ? `hoy ${today ? "incluido" : "no incluido"} · ${next ? "se activa" : "se quita"} al próximo pago`
                        : `hoy ${today ? "incluido" : "no incluido"}`}
                    </span>
                  </label>
                );
              })}
            </div>
            <small style={{ color: BRAND_COLORS.text.muted, fontSize: '0.7rem' }}>
              Lo que marques aquí entra solo en el corte del ciclo (o en la fecha de abajo) y se cobra desde ese pago.
            </small>
          </div>
          {/* Solo con un cambio pendiente: fecha explícita (cortesías de N días) y "Aplicar ahora" (pruebas). */}
          {addonsPending && (
            <div className="app-form-group">
              <label className="app-form-label small" style={{ color: BRAND_COLORS.text.secondary, fontSize: '0.8rem' }}>Aplicar add-ons el (opcional)</label>
              <input
                className="app-form-control"
                type="date"
                value={editingData.addonsNextCycleAt || ""}
                onChange={(e) => setEditingData((ed) => ({ ...ed, addonsNextCycleAt: e.target.value }))}
              />
              <small style={{ color: BRAND_COLORS.text.muted, fontSize: '0.7rem' }}>
                Vacío: con el corte{editingData.subscriptionExpiresAt ? ` (${editingData.subscriptionExpiresAt})` : ""}. Con fecha: al final de ese día. Guarda para programarlo.
              </small>
              {onApplyAddonsNow && (
                <button type="button" className="sa-btn sa-btn-sm mt-2" onClick={onApplyAddonsNow}>
                  Aplicar ahora (sin esperar al corte)
                </button>
              )}
            </div>
          )}
          <div className="app-form-group">
            <label className="app-form-label small" style={{ color: BRAND_COLORS.text.secondary, fontSize: '0.8rem' }}>Precio mensual vigente</label>
            <div className="app-form-control d-flex align-items-center" style={{ backgroundColor: BRAND_COLORS.backgrounds.tertiary, color: BRAND_COLORS.primary.main, borderColor: BRAND_COLORS.borders.light, fontWeight: 700 }}>
              ${computePrice(editingData.seats, editingData.addons)} MXN
            </div>
            {addonsPending && (
              <small style={{ color: BRAND_COLORS.warning, fontSize: '0.7rem' }}>
                Desde el próximo ciclo: ${computePrice(editingData.seats, editingData.addonsNextCycle)} MXN
              </small>
            )}
          </div>
          <div className="app-form-group">
            <label className="app-form-label small" style={{ color: BRAND_COLORS.text.secondary, fontSize: '0.8rem' }}>Fecha de Inicio</label>
            <input
              className="app-form-control"
              type="date"
              value={editingData.subscriptionStartDate || ""}
              onChange={(e) =>
                setEditingData((ed) => ({
                  ...ed,
                  subscriptionStartDate: e.target.value,
                }))
              }
            />
          </div>

          {/* Vigencia (expiresAt) editable manualmente. Útil para alta
              inicial / cortesías / ajustes; los pagos también la mueven. */}
          <div className="app-form-group">
            <label className="app-form-label small" style={{ color: BRAND_COLORS.text.secondary, fontSize: '0.8rem' }}>Vigencia hasta (manual)</label>
            <input
              className="app-form-control"
              type="date"
              value={editingData.subscriptionExpiresAt || ""}
              onChange={(e) =>
                setEditingData((ed) => ({
                  ...ed,
                  subscriptionExpiresAt: e.target.value,
                }))
              }
            />
          </div>

          {/* Resumen de vigencia: preview en vivo del campo de arriba */}
          <div className="app-form-group" style={{ gridColumn: '1 / -1' }}>
            <label className="app-form-label small" style={{ color: BRAND_COLORS.text.secondary, fontSize: '0.8rem' }}>Estado de la suscripción</label>
            {(() => {
              const st = getSubscriptionStatus(editingData.subscriptionExpiresAt, editingData.status);
              return (
                <div style={{
                  backgroundColor: BRAND_COLORS.backgrounds.tertiary,
                  border: `1px solid ${BRAND_COLORS.borders.light}`,
                  borderLeft: `3px solid ${st.color}`,
                  borderRadius: 8,
                  padding: '0.75rem 0.9rem',
                }}>
                  <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      color: st.color, fontWeight: 700, fontSize: '0.85rem',
                    }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: st.color, display: 'inline-block' }} />
                      {st.label}
                    </span>
                    {st.relative && (
                      <span style={{ color: BRAND_COLORS.text.muted, fontSize: '0.72rem' }}>{st.relative}</span>
                    )}
                  </div>
                  <div style={{ color: BRAND_COLORS.text.primary, fontSize: '0.9rem', fontWeight: 600 }}>
                    {st.expiresLabel ? `Vigente hasta ${st.expiresLabel}` : 'Sin fecha de vigencia'}
                  </div>
                  <div style={{ color: BRAND_COLORS.text.muted, fontSize: '0.68rem', marginTop: 2 }}>
                    Editable arriba para ajustes manuales; registrar un pago también la extiende.
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </>
  );
}
